import {
  Col,
  Collapse,
  type CollapseProps,
  Form,
  type FormInstance,
  Row,
  Switch,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '@client/components/price/Rebate/rebate.module.less';
import { mergeNames } from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';
import { prettifyCamelCase } from '@common/utils';

import type { EDiscountType } from '@common/types';

type TDiscountedComponentProps = {
  formRef: FormInstance<any>;
  disabled: boolean;
  collapsible: boolean;
  loading: boolean;
  discountHeader?: string;
  canBeDiscounted?: boolean;
  isDiscounted?: boolean;
  renderScheduler?: (readOnlyFields?: string[]) => React.ReactNode;
  prefix?: string[];
  discountTypes?: (keyof typeof EDiscountType)[];
  readOnlyFields?: string[];
  namespace?: string;
};

export const DiscountedComponent: React.FC<TDiscountedComponentProps> = (
  props,
) => {
  const {
    formRef,
    collapsible = false,
    disabled,
    loading,
    discountHeader,
    canBeDiscounted = false,
    renderScheduler,
    prefix = [],
    readOnlyFields = [],
  } = props;

  const { t } = useTranslation();

  const _discountPanelHeader =
    `${prettifyCamelCase(discountHeader as string)}.discount`
      .toLowerCase()
      .replace(/ /g, '-');

  const isDiscounted = Form.useWatch(
    mergeNames(prefix, 'isDiscounted'),
    formRef,
  );
  const discounted = !!isDiscounted;
  const [activeKey, setActiveKey] = useState<string[]>(null);

  useEffect(() => {
    setActiveKey(discounted ? [_discountPanelHeader] : null);
  }, [_discountPanelHeader, discounted]);

  const discountItems: CollapseProps['items'] = [
    {
      key: _discountPanelHeader,
      label: discountHeader || t('price.discountedPrice'),
      extra: (
        <div
          onClick={(e) => e.stopPropagation()}
          className={styles.extraSwitch}
        >
          <Form.Item
            layout="horizontal"
            name={mergeNames(prefix, 'isDiscounted')}
            label={t('price.discounted')}
            valuePropName="checked"
          >
            <Switch disabled={disabled || !canBeDiscounted} loading={loading} />
          </Form.Item>
        </div>
      ),
      children: renderScheduler ? (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            {renderScheduler(readOnlyFields)}
          </Col>
        </Row>
      ) : null,
    },
  ];

  return canBeDiscounted ? (
    <Collapse
      items={discountItems}
      className={styles.discount}
      collapsible={collapsible ? 'header' : 'disabled'}
      activeKey={activeKey as string[]}
    />
  ) : null;
};
